import React, { useState } from "react";
import { createPortal } from "react-dom";
import { m as motion, MAnimatePresence as AnimatePresence } from "../shared/components/Motion";
import "./preview-modal.css";
import { modalVariants, childVariants } from "shared/utils";
const PreviewModal = ({
  isOpen,
  onClose,
  image,
  title,
  platform,
  logo,
  issuedOn,
  credentialId,
  link,
  description,
  skills = [],
  keyLearnings = [],
  codelink,
  demolink,
  modelListItems,
  projectDescription,
}) => {
  const [imageError, setImageError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const modelItems = modelListItems ? modelListItems.split("|") : [];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            ></motion.i>
            {/* Title & Description */}
            <motion.h3
              className="preview-modal-title"
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {title}
            </motion.h3>
            {description && (
              <motion.p
                className="preview-modal-description"
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {description}
              </motion.p>
            )}
            <div className="preview-modal-container">
              {/* Left Side: Image */}
              <motion.div
                className={`preview-modal-image ${
                  keyLearnings.length > 0 && "project-img"
                }`}
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {imageError && (
                  <div className="image-error__others img-err_custom">
                    <i className="uil uil-image-slash"></i>
                  </div>
                )}
                {image && (
                  <img
                    src={image}
                    alt={title}
                    className={`${keyLearnings.length > 0 && "project-img"}`}
                    onError={() => setImageError(true)}
                    style={{ display: imageError ? "none" : "block" }}
                  />
                )}
              </motion.div>

              {/* Right Side: Details */}
              <motion.div
                className="preview-modal-details"
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Certificate-Specific Info */}
                {projectDescription && (
                  <>
                    <motion.h4
                      className="preview-modal-subtitle"
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      Description
                    </motion.h4>
                    <motion.p
                      className="preview-modal-items"
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {projectDescription}
                    </motion.p>
                  </>
                )}
                {platform && (
                  <motion.div
                    className="preview-modal-header"
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {logoError && (
                      <div
                        className="image-error__others img-err_custom"
                        style={{
                          width: "40px",
                          height: "40px",
                          boxShadow: "none",
                          fontSize: "1rem",
                        }}
                      >
                        <i className="uil uil-image-slash"></i>
                      </div>
                    )}
                    <img
                      src={logo}
                      alt={platform}
                      className="preview-platform-logo"
                      onError={() => setLogoError(true)}
                      style={{ display: logoError ? "none" : "block" }}
                    />
                    <div className="preview-modal-info-group">
                      <p className="preview-modal-platform">{platform}</p>
                      <p className="preview-modal-issued">{issuedOn}</p>
                      <p className="preview-modal-credential">
                        Credential ID: {credentialId}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Skills List */}
                {modelItems.length > 0 && (
                  <motion.ul
                    className="preview-modal-certificates"
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {modelItems.map((item, i) => (
                      <motion.li
                        key={i}
                        className="preview-modal-service"
                        variants={childVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                      >
                        <i className="uil uil-check-circle preview-modal-icon"></i>
                        <p className="preview-modal-info">{item}</p>
                      </motion.li>
                    ))}
                  </motion.ul>
                )}

                {/* Skills */}
                {skills.length > 0 && (
                  <>
                    <motion.h4
                      className="preview-modal-subtitle"
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      Skills
                    </motion.h4>
                    <motion.ul
                      className="preview-modal-skills"
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {skills.map((skill, i) => (
                        <motion.li
                          key={i}
                          className="preview-modal-skill"
                          variants={childVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                        >
                          {skill}
                        </motion.li>
                      ))}
                    </motion.ul>
                  </>
                )}

                {/* View Certificate Button */}
                {link && (
                  <motion.a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="preview-link"
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    View on site
                  </motion.a>
                )}
              </motion.div>
            </div>
            <motion.div
              className="preview-model-bottom"
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Key Learnings */}
              {keyLearnings.length > 0 && (
                <>
                  <motion.h4
                    className="preview-modal-subtitle"
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    Key Learnings
                  </motion.h4>
                  <motion.ul
                    className="preview-modal-listItems"
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {keyLearnings.map((learning, i) => (
                      <motion.li
                        key={i}
                        className="preview-modal-service"
                        variants={childVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                      >
                        <i className="uil uil-check-circle preview-modal-icon"></i>{" "}
                        {learning}
                      </motion.li>
                    ))}
                  </motion.ul>
                </>
              )}
              {/* Links */}
              {(codelink || demolink) && (
                <motion.div
                  className="preview-modal-buttons"
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {codelink && (
                    <motion.a
                      href={codelink}
                      target="_blank"
                      rel="noreferrer"
                      className="preview-link"
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      View Code
                    </motion.a>
                  )}
                  {demolink && (
                    <motion.a
                      href={demolink}
                      target="_blank"
                      rel="noreferrer"
                      className="preview-link"
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      Live Demo
                    </motion.a>
                  )}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PreviewModal;
